import React from 'react'
import {
  IndianRupee,
  Wallet,
  TrendingUp,
  Archive,
  Milestone,
  CalendarRange
} from 'lucide-react'
import contractReportsService from '../../../services/contractReportsService'
import {
  ReportShell,
  ReportSection,
  ReportTable,
  KpiGrid,
  EmptyState,
  ShareBar,
  useContractReport,
  toInputDate,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatDate,
  primaryCurrency,
  breakdownColumns,
  breakdownFooter,
  summarySection
} from './reportShared'

const DEFAULT_FILTERS = { asOf: toInputDate(), fromDate: '', toDate: '' }

const currencyCols = [
  { key: 'currency', label: 'Currency' },
  { key: 'count', label: 'Contracts', align: 'right', render: (r) => formatNumber(r.count) },
  {
    key: 'activeValue',
    label: 'Active Value',
    align: 'right',
    render: (r) => formatMoney(r.activeValue, r.currency),
    exportValue: (r) => r.activeValue
  },
  {
    key: 'value',
    label: 'Total Value',
    align: 'right',
    render: (r) => formatMoney(r.value, r.currency),
    exportValue: (r) => r.value
  }
]

const milestoneCols = (rows, currency) =>
  breakdownColumns('status', 'Milestone Status', rows.map((r) => ({ ...r, value: r.amount })), currency, { badge: true })

const trendCols = (months, currency) => {
  const max = Math.max(0, ...months.map((m) => m.value))
  return [
    { key: 'label', label: 'Month' },
    { key: 'count', label: 'Contracts Signed', align: 'right', render: (r) => formatNumber(r.count) },
    {
      key: 'value',
      label: 'Signed Value',
      align: 'right',
      render: (r) => formatMoney(r.value, currency),
      exportValue: (r) => r.value
    },
    { key: 'bar', label: '', render: (r) => <ShareBar value={r.value} total={max} />, export: false }
  ]
}

const ContractFinancialReport = () => {
  const report = useContractReport(contractReportsService.getFinancialReport, DEFAULT_FILTERS)

  const getExportSections = (data) => {
    const cur = primaryCurrency(data.meta)
    const s = data.summary
    const u = data.renewalUplift
    const milestoneRows = data.milestoneBilling.byStatus.map((r) => ({ ...r, value: r.amount }))
    return [
      summarySection('Summary', [
        ['TCV (active)', s.tcvActive],
        ['TCV (all statuses)', s.tcvAll],
        ['Active contracts', s.activeCount],
        ['Total contracts', s.totalCount],
        ['Average active value', s.avgActiveValue],
        ['Renewal value (total)', u.totalRenewalValue],
        ['Renewal uplift (increases)', u.totalIncrease],
        ['Renewal reductions', u.totalDecrease],
        ['Renewal net change', u.netChange],
        ['Milestone amount (total)', data.milestoneBilling.totalAmount],
        ['Archived contracts', data.archived.count],
        ['Archived value', data.archived.totalValue],
        ['Currencies', (data.meta.currencies || []).join(', ')]
      ]),
      { title: 'By Currency', columns: currencyCols, rows: data.byCurrency },
      { title: 'By Type', columns: breakdownColumns('type', 'Contract Type', data.byType, cur), rows: data.byType },
      { title: 'By Status', columns: breakdownColumns('status', 'Status', data.byStatus, cur), rows: data.byStatus },
      {
        title: 'By Billing Frequency',
        columns: breakdownColumns('billingFrequency', 'Billing Frequency', data.byBillingFrequency, cur),
        rows: data.byBillingFrequency
      },
      { title: 'Milestone Billing', columns: milestoneCols(data.milestoneBilling.byStatus, cur), rows: milestoneRows },
      { title: 'Monthly Trend', columns: trendCols(data.monthlyTrend.months, cur), rows: data.monthlyTrend.months },
      { title: 'Archived by Reason', columns: breakdownColumns('reason', 'Archive Reason', data.archived.byReason, cur), rows: data.archived.byReason }
    ]
  }

  return (
    <ReportShell
      title="Financial Reports"
      subtitle="Contract value, billing and revenue impact"
      path="/contracts/reports/financial"
      report={report}
      filterFields={['asOf', 'fromDate', 'toDate']}
      filterHint="Period filters contracts by signed date, renewals by renewal date and archives by archive date"
      exportName="Contract_Financials"
      getExportSections={getExportSections}
    >
      {(data) => {
        const s = data.summary
        const u = data.renewalUplift
        const mb = data.milestoneBilling
        const trend = data.monthlyTrend
        const cur = primaryCurrency(data.meta)
        const milestoneRows = mb.byStatus.map((r) => ({ ...r, value: r.amount }))
        if (s.totalCount === 0 && data.archived.count === 0 && u.renewalCount === 0 && trend.totalCount === 0) {
          return <EmptyState message="No contract financial data found for the selected period" />
        }
        return (
          <>
            <KpiGrid
              items={[
                {
                  icon: IndianRupee,
                  label: 'TCV — Active',
                  value: formatMoneyCompact(s.tcvActive, cur),
                  title: formatMoney(s.tcvActive, cur),
                  subtitle: `${formatNumber(s.activeCount)} active contracts`
                },
                {
                  icon: Wallet,
                  label: 'TCV — All Statuses',
                  value: formatMoneyCompact(s.tcvAll, cur),
                  title: formatMoney(s.tcvAll, cur),
                  subtitle: `${formatNumber(s.totalCount)} contracts`
                },
                {
                  icon: TrendingUp,
                  label: 'Renewal Uplift',
                  value: u.renewalsWithValueChange ? formatMoneyCompact(u.netChange, cur) : 'N/A',
                  title: u.note || `Increases ${formatMoney(u.totalIncrease, cur)} · reductions ${formatMoney(u.totalDecrease, cur)}`,
                  subtitle: u.renewalsWithValueChange
                    ? `${u.increaseCount} up · ${u.decreaseCount} down`
                    : 'No value changes recorded'
                },
                {
                  icon: Archive,
                  label: 'Archived Value',
                  value: formatMoneyCompact(data.archived.totalValue, cur),
                  title: formatMoney(data.archived.totalValue, cur),
                  subtitle: `${formatNumber(data.archived.count)} archived contracts`
                }
              ]}
            />

            <ReportSection title="Value by Currency" subtitle="Amounts shown in their native currency (no FX conversion)">
              <ReportTable columns={currencyCols} rows={data.byCurrency} emptyText="No signed contracts" />
            </ReportSection>

            <ReportSection
              title="Monthly Signing Trend"
              subtitle={`Contracts by signed date, ${formatDate(trend.from)} – ${formatDate(trend.to)}`}
              count={`${formatNumber(trend.totalCount)} signed · ${formatMoneyCompact(trend.totalValue, cur)}`}
            >
              <ReportTable
                columns={trendCols(trend.months, cur)}
                rows={trend.months}
                footer={{ label: 'Total', count: formatNumber(trend.totalCount), value: formatMoney(trend.totalValue, cur) }}
              />
            </ReportSection>

            <div className="contract-report-grid-2">
              <ReportSection title="Value by Contract Type">
                <ReportTable
                  columns={breakdownColumns('type', 'Contract Type', data.byType, cur)}
                  rows={data.byType}
                  footer={breakdownFooter('type', data.byType, cur)}
                  emptyText="No signed contracts"
                />
              </ReportSection>
              <ReportSection title="Value by Status">
                <ReportTable
                  columns={breakdownColumns('status', 'Status', data.byStatus, cur, { badge: true })}
                  rows={data.byStatus}
                  footer={breakdownFooter('status', data.byStatus, cur)}
                  emptyText="No signed contracts"
                />
              </ReportSection>
            </div>

            <div className="contract-report-grid-2">
              <ReportSection title="Value by Billing Frequency">
                <ReportTable
                  columns={breakdownColumns('billingFrequency', 'Billing Frequency', data.byBillingFrequency, cur)}
                  rows={data.byBillingFrequency}
                  emptyText="No signed contracts"
                />
              </ReportSection>
              <ReportSection
                title="Milestone Billing"
                subtitle="Milestone amounts on signed contracts by milestone status"
                count={`${formatNumber(mb.totalMilestones)} milestones · ${formatMoneyCompact(mb.totalAmount, cur)}`}
              >
                <ReportTable
                  columns={milestoneCols(mb.byStatus, cur)}
                  rows={milestoneRows}
                  footer={breakdownFooter('status', milestoneRows, cur)}
                  emptyText="No milestones with amounts recorded"
                />
              </ReportSection>
            </div>

            <div className="contract-report-grid-2">
              <ReportSection
                title="Renewal Value Changes"
                subtitle="From renewal records that capture old and new contract values"
                count={`${u.renewalCount} renewals`}
              >
                {u.note ? (
                  <div className="contract-report-empty">{u.note}</div>
                ) : (
                  <ReportTable
                    columns={[
                      { key: 'label', label: 'Change' },
                      { key: 'count', label: 'Renewals', align: 'right' },
                      { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatMoney(r.amount, cur) }
                    ]}
                    rows={[
                      { id: 'up', label: 'Increases', count: u.increaseCount, amount: u.totalIncrease },
                      { id: 'down', label: 'Reductions', count: u.decreaseCount, amount: u.totalDecrease },
                      { id: 'net', label: 'Net change', count: u.renewalsWithValueChange, amount: u.netChange }
                    ]}
                  />
                )}
                <div className="contract-report-empty" style={{ textAlign: 'left', borderTop: '1px solid #eef0f4' }}>
                  <Milestone size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Total renewal value in period: <strong>{formatMoney(u.totalRenewalValue, cur)}</strong>
                </div>
              </ReportSection>
              <ReportSection title="Archived Contracts by Reason" count={`${data.archived.count} archived`}>
                <ReportTable
                  columns={breakdownColumns('reason', 'Archive Reason', data.archived.byReason, cur)}
                  rows={data.archived.byReason}
                  footer={data.archived.byReason.length ? breakdownFooter('reason', data.archived.byReason, cur) : undefined}
                  emptyText="No archived contracts"
                />
              </ReportSection>
            </div>

            {trend.totalCount === 0 && (
              <div className="contract-report-note info">
                <CalendarRange size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>No contracts were signed in the 12-month trend window.</span>
              </div>
            )}
          </>
        )
      }}
    </ReportShell>
  )
}

export default ContractFinancialReport
